import { Component, computed, effect, inject, signal } from '@angular/core';
import { hasConsent } from '@app/helpers/consent';
import { GaService } from '@app/services/ga.service';
import { FormAutofillService } from '@app/services/form-autofill.service';
import { RokkaLoaderService } from '@app/services/rokka-loader.service';

// The premium calculator serves agency photos as square 720x720 images, so a
// person photo is rendered at the same size and ratio.
const PERSON_IMAGE_WIDTH = 720;
const PERSON_IMAGE_RATIO = 1;

@Component({
  standalone: false,
  selector: 'webform-gabox',
  templateUrl: './gabox.component.html',
  styleUrls: ['./gabox.component.scss'],
})
export class GaboxComponent {
  ga = computed(() => this.#gaService.currentGa());
  imageUrl = computed(() => {
    const ga = this.ga();
    if (!ga) return '';
    // A Marktbearbeiter brings their own photo. The agency photo has no such
    // URL and is derived from the agency number, which would show the general
    // agent rather than the person named in the box.
    if (ga.contactPersonImage) return this.#squareImageUrl(ga.contactPersonImage);

    return `https://secure.mobiliar.ch/vvn-public/praemienrechner/rest/contact/generalagency/${ga.generalAgencyNumber}/contactpersonimage`;
  });

  constructor() {
    effect(() => {
      if (this.#autofill.loginControlUsed()) {
        void this.#gaService.selectMarktbearbeiterForCurrentZip();
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('OneTrustGroupsUpdated', () => {
        this.#consentRefresh.update((v) => v + 1);
      });
    }
  }

  #consentRefresh = signal(0);
  showConsentPlaceholder = computed(() => {
    this.#consentRefresh(); // Track for reactivity
    return !hasConsent();
  });

  /**
   * Turns the plain Rokka URL of the API into a square rendition.
   *
   * The API delivers the unprocessed image, so it keeps the ratio it was
   * uploaded with. Returns the URL untouched when it is not a Rokka one.
   */
  #squareImageUrl(url: string): string {
    const [hash, filename] = url.split('/noop/')[1]?.split('/') || [];
    if (!hash || !filename) return url;

    return this.#rokka.buildUrl(
      { hash, filename, styleId: 'person_square' },
      PERSON_IMAGE_WIDTH,
      PERSON_IMAGE_RATIO
    );
  }

  #gaService = inject(GaService);
  #autofill = inject(FormAutofillService);
  #rokka = inject(RokkaLoaderService);
}
